(() => {
  if (customElements.get('featured-subscription-form')) return;

  class FeaturedSubscriptionForm extends HTMLElement {
    constructor() {
      super();
      this.onOptionChange = this.onOptionChange.bind(this);
      this.onPlanSelectChange = this.onPlanSelectChange.bind(this);
      this.onPlanSelectClick = this.onPlanSelectClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onMasterSelectChange = this.onMasterSelectChange.bind(this);
    }

    connectedCallback() {
      this.productFormId = this.dataset.productFormId;
      this.productSectionId = this.dataset.productSectionId;
      this.activeVariantId = '';
      this.variantData = this.parseVariantData();
      this.sellingPlanInput = this.getSellingPlanInput();
      this.optionInputs = this.querySelectorAll('[data-js-purchase-option]');
      this.planSelect = this.querySelector('[data-js-selling-plan-select]');
      this.subscriptionOption = this.querySelector('[data-js-subscription-option]');
      this.onetimeOption = this.querySelector('[data-js-onetime-option]');
      this.productSection = document.getElementById(`ProductSection-${this.productSectionId}`);
      this.variantSelect = this.getVariantSelect();

      if (!this.variantData || !this.optionInputs.length) return;

      this.optionInputs.forEach((input) => {
        input.addEventListener('change', this.onOptionChange);
      });

      if (this.planSelect) {
        this.planSelect.addEventListener('change', this.onPlanSelectChange);
        this.planSelect.addEventListener('click', this.onPlanSelectClick);
      }

      if (this.productSection) {
        this.productSection.addEventListener('variantChange', this.onVariantChange);
      }

      document.addEventListener('variant:change', this.onVariantChange);

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', this.onMasterSelectChange);
      }

      this.updateForVariant(this.getCurrentVariantId(), false);
    }

    disconnectedCallback() {
      this.optionInputs?.forEach((input) => {
        input.removeEventListener('change', this.onOptionChange);
      });

      if (this.planSelect) {
        this.planSelect.removeEventListener('change', this.onPlanSelectChange);
        this.planSelect.removeEventListener('click', this.onPlanSelectClick);
      }

      if (this.productSection) {
        this.productSection.removeEventListener('variantChange', this.onVariantChange);
      }

      document.removeEventListener('variant:change', this.onVariantChange);

      if (this.variantSelect) {
        this.variantSelect.removeEventListener('change', this.onMasterSelectChange);
      }
    }

    parseVariantData() {
      const dataEl = this.querySelector('[data-js-featured-subscription-variant-data]');
      if (!dataEl) return null;

      try {
        return JSON.parse(dataEl.textContent);
      } catch (error) {
        return null;
      }
    }

    getSellingPlanInput() {
      const form = document.getElementById(this.productFormId);
      return (
        form?.querySelector('[data-js-selling-plan-input]') ||
        document.querySelector(`[form="${this.productFormId}"][data-js-selling-plan-input]`)
      );
    }

    getVariantSelect() {
      const form = document.getElementById(this.productFormId);
      return form?.querySelector('[name="id"]') || this.productSection?.querySelector('[data-product-select]');
    }

    getCurrentVariantId() {
      const variantInput = this.variantSelect || this.getVariantSelect();
      return variantInput?.value || String(this.variantData?.currentVariantId || '');
    }

    getActiveVariant() {
      const variantId = this.activeVariantId || this.getCurrentVariantId();
      return this.variantData?.variants?.[variantId] || null;
    }

    getVariantIdFromEvent(event) {
      const eventVariantId = event?.detail?.variant?.id;
      if (eventVariantId) {
        return String(eventVariantId);
      }

      return this.getCurrentVariantId();
    }

    getFormWrapper() {
      return this.closest('[data-js-featured-subscription-wrapper]');
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents);
      }

      return String(cents);
    }

    calcSaveAmount(compareCents, priceCents) {
      const compare = parseInt(compareCents, 10);
      const price = parseInt(priceCents, 10);

      if (!compare || !price || compare <= price) return 0;

      return compare - price;
    }

    getPlanSaveCents(plan, variant) {
      const saveFromPlan = parseInt(plan?.saveCents, 10);
      if (saveFromPlan > 0) {
        return saveFromPlan;
      }

      return this.calcSaveAmount(variant?.onetime?.priceCents, plan?.priceCents);
    }

    getPerk1Template() {
      return this.getAttribute('data-perk-1-template') || '';
    }

    replaceTemplatePlaceholders(template, { save, price, percent }) {
      if (!template) return '';

      let output = template;

      if (save) {
        output = output.replace(/\[save\]/g, save);
      }

      if (price) {
        output = output.replace(/\[price\]/g, price);
      }

      if (percent) {
        output = output.replace(/\[percent\]/g, percent);
      }

      return output;
    }

    applySavePlaceholder(template, saveAmountCents) {
      if (!template?.includes('[save]') || !saveAmountCents) {
        return template;
      }

      return this.replaceTemplatePlaceholders(template, {
        save: this.formatMoney(saveAmountCents),
      });
    }

    getSubscribeSaveTemplate() {
      return this.getAttribute('data-subscribe-save-template') || '';
    }

    getSubscribeTemplate() {
      return this.getAttribute('data-subscribe-template') || 'Subscribe & Save';
    }

    buildOnetimeTitle(onetime) {
      const template = this.dataset.onetimeTemplate || 'One-time purchase';
      const price = onetime.price || this.formatMoney(onetime.priceCents);

      if (template.includes('[price]')) {
        return this.replaceTemplatePlaceholders(template, { price });
      }

      return `${template} ${price}`.trim();
    }

    buildSubscriptionTitle(plan, variant) {
      const saveTemplate = this.getSubscribeSaveTemplate();
      const defaultTemplate = this.getSubscribeTemplate();
      const saveAmountCents = this.getPlanSaveCents(plan, variant);

      if (saveTemplate.includes('[save]')) {
        if (saveAmountCents > 0) {
          return this.applySavePlaceholder(saveTemplate, saveAmountCents);
        }

        return defaultTemplate;
      }

      return saveTemplate || defaultTemplate;
    }

    updatePerks(plan, variant) {
      const perkTemplate = this.getPerk1Template();
      const perkEl = this.querySelector('[data-js-featured-subscription-perk-1]');

      if (!perkEl || !perkTemplate) return;

      const saveAmountCents = this.getPlanSaveCents(plan, variant);

      perkEl.textContent = this.applySavePlaceholder(perkTemplate, saveAmountCents);
    }

    isEventForThisProduct(event) {
      const variantId = event.detail?.variant?.id;
      if (!variantId) return true;

      return Boolean(this.variantData?.variants?.[String(variantId)]);
    }

    shouldShowForm(variant) {
      if (!variant) return false;

      return (
        Boolean(variant.available) &&
        !variant.quantityRuleSoldout &&
        Boolean(variant.hasSubscription)
      );
    }

    updateFormVisibility(variant) {
      const show = this.shouldShowForm(variant);
      const wrapper = this.getFormWrapper();

      if (wrapper) {
        wrapper.hidden = !show;
      }

      if (this.sellingPlanInput && !show) {
        this.sellingPlanInput.value = '';
        this.sellingPlanInput.disabled = true;
      }

      return show;
    }

    onVariantChange(event) {
      if (!this.isEventForThisProduct(event)) return;
      this.updateForVariant(this.getVariantIdFromEvent(event), true);
    }

    onMasterSelectChange() {
      this.updateForVariant(this.getCurrentVariantId(), true);
    }

    onPlanSelectClick(event) {
      event.stopPropagation();
    }

    onPlanSelectChange() {
      const subscriptionInput = this.querySelector('[data-js-purchase-option][value="subscription"]');
      if (subscriptionInput && !subscriptionInput.checked) {
        subscriptionInput.checked = true;
      }

      this.syncSubscriptionOptionFromSelect();

      if (subscriptionInput?.checked) {
        this.applyOption(subscriptionInput);
      }
    }

    onOptionChange(event) {
      if (event.target === document.activeElement) {
        event.target.blur();
      }

      this.applyOption(event.target);
    }

    updateForVariant(variantId, preserveSelection) {
      const variant = this.variantData?.variants?.[variantId];
      if (!variant) return;

      this.activeVariantId = variantId;

      if (!this.updateFormVisibility(variant)) {
        return;
      }

      const selectedPurchaseType = preserveSelection
        ? this.querySelector('[data-js-purchase-option]:checked')?.value
        : null;

      if (this.subscriptionOption) {
        this.subscriptionOption.hidden = !variant.hasSubscription;
      }

      this.rebuildPlanSelect(variant);
      this.updateOnetimeOption(variant);

      if (!variant.hasSubscription) {
        const onetimeInput = this.querySelector('[data-js-purchase-option][value="onetime"]');
        if (onetimeInput) {
          onetimeInput.checked = true;
          this.applyOption(onetimeInput);
        }
        return;
      }

      this.syncSubscriptionOptionFromSelect();

      const defaultOption = this.dataset.defaultOption || 'subscription';
      const nextOption =
        selectedPurchaseType ||
        (defaultOption === 'subscription' && variant.hasSubscription ? 'subscription' : 'onetime');

      const nextInput = this.querySelector(`[data-js-purchase-option][value="${nextOption}"]`);
      if (nextInput) {
        nextInput.checked = true;
        this.applyOption(nextInput);
      }
    }

    rebuildPlanSelect(variant) {
      if (!this.planSelect || !this.subscriptionOption) return;

      this.planSelect.innerHTML = '';

      if (!variant.plans?.length) {
        return;
      }

      variant.plans.forEach((plan, index) => {
        const option = document.createElement('option');
        option.value = plan.id;
        option.textContent = plan.name;
        if (index === 0) option.selected = true;
        this.planSelect.appendChild(option);
      });

      this.syncSubscriptionOptionFromSelect();
    }

    updateOnetimeOption(variant) {
      if (!this.onetimeOption || !variant.onetime) return;

      const titleEl = this.onetimeOption.querySelector('[data-js-onetime-title]');
      const currentEl = this.onetimeOption.querySelector('[data-js-onetime-current]');

      if (currentEl) {
        currentEl.textContent = variant.onetime.price || '';
      }

      if (titleEl) {
        titleEl.textContent = this.buildOnetimeTitle(variant.onetime);
      }
    }

    syncSubscriptionOptionFromSelect() {
      if (!this.planSelect || !this.subscriptionOption) return;

      const selectedOption = this.planSelect.selectedOptions[0];
      if (!selectedOption) return;

      const variant = this.getActiveVariant();
      const plan = variant?.plans?.find((item) => String(item.id) === String(selectedOption.value));
      if (!plan) return;

      this.subscriptionOption.dataset.sellingPlanId = plan.id;

      const currentEl = this.subscriptionOption.querySelector('[data-js-subscription-current]');
      const titleEl = this.subscriptionOption.querySelector('[data-js-subscription-title]');

      if (currentEl) {
        currentEl.textContent = plan.price || '';
      }

      if (titleEl) {
        titleEl.textContent = this.buildSubscriptionTitle(plan, variant);
      }

      this.updatePerks(plan, variant);
    }

    applyOption(input) {
      const option = input.closest('[data-purchase-option]');
      if (!option) return;

      if (input.value === 'subscription') {
        this.syncSubscriptionOptionFromSelect();
      }

      this.querySelectorAll('[data-purchase-option]').forEach((item) => {
        item.classList.toggle('is-selected', item === option);
      });

      const purchaseType = input.value;
      const sellingPlanId = option.dataset.sellingPlanId || '';

      if (this.sellingPlanInput) {
        this.sellingPlanInput.value = purchaseType === 'subscription' ? sellingPlanId : '';
        this.sellingPlanInput.disabled = purchaseType !== 'subscription' || !sellingPlanId;
      }

      this.dispatchEvent(
        new CustomEvent('featured-subscription-form:change', {
          bubbles: true,
          detail: {
            purchaseType,
            sellingPlanId,
          },
        })
      );
    }
  }

  customElements.define('featured-subscription-form', FeaturedSubscriptionForm);
})();
